// Loads a Data QA run's weekly series file (schema v2) once `enabled` turns
// true -- e.g. once a Request Forecast card becomes a scaling card, which
// anchors its max weekly spend on the highest observed week. It's ~MBs for big
// runs, so it isn't fetched up front.
import { useQuery } from '@tanstack/react-query';
import { DataQaSeries } from './dataQaContract';
import { fetchDataQaSeries } from './useForecastDataQa';

// A transient S3 error would otherwise leave every scaling card without its
// highest-week check, so retry a few times (with React Query's backoff).
const SERIES_RETRIES = 3;

export const useDataQaSeries = (
  s3Key: string | undefined,
  seriesFile: string | undefined,
  enabled: boolean
) =>
  useQuery<DataQaSeries>({
    queryKey: ['dataQaSeries', s3Key, seriesFile],
    queryFn: () => {
      if (!s3Key || !seriesFile) {
        throw new Error('No Data QA series file to load.');
      }
      return fetchDataQaSeries(s3Key, seriesFile);
    },
    enabled: enabled && Boolean(s3Key && seriesFile),
    // A QA run's series file never changes.
    staleTime: Infinity,
    retry: SERIES_RETRIES,
    // The card shows its own "Try again"; don't also raise the global error modal.
    meta: { suppressGlobalError: true }
  });
